import {getCustomerEndpoint, postCustomerEndpoint} from "./CustomerRequester";
import {customerId as currentCustomerId} from "./services/LoginService";

/*
 * Terms of use consent ("Nutzungsbedingungen SepaHeld®").
 *
 *   GET  customers/{customerId}/terms               -> TermsStatus
 *   POST customers/{customerId}/terms/accept        {version} -> TermsStatus
 *   POST customers/{customerId}/terms/remind-later  -> TermsStatus
 *
 * TermsStatus: {currentVersion, termsUrl, pdfUrl, required, accepted, acceptedAt,
 * acceptedBy, canAccept, remindLaterAllowed, remindersLeft, reminderDeadline (nullable),
 * legacyPlan (true = old generation-1 tariff; picks the copy variant)}
 *
 * checkAuthorization is off on purpose: a 403 ("you may not accept for this
 * company") must not log the user out.
 */
const OPTS = {checkAuthorization: false};

// Turns synchronous throws into rejections so callers only need .catch().
const call = (request) => new Promise((resolve) => resolve(request()))
    .then((response) => response?.data ?? null);

// The consent modal (app shell) and the checkout checkbox (plan page / paywall) both need the status
// when a page opens; they share one request and its result for a short time (like the 30 s staleTime
// of the query layer). Keyed by customer, so a different login never sees another company's status.
const STATUS_TTL_MS = 30_000;
let statusCache = null; // {customer, promise, at}

export function getTermsStatus() {
    const customer = currentCustomerId();
    const now = Date.now();
    if (statusCache && statusCache.customer === customer && now - statusCache.at < STATUS_TTL_MS) {
        return statusCache.promise;
    }
    const promise = call(() => getCustomerEndpoint("terms", OPTS));
    statusCache = {customer, promise, at: now};
    // A failed request must not be served from the cache.
    promise.catch(() => {
        if (statusCache?.promise === promise) {
            statusCache = null;
        }
    });
    return promise;
}

// Accepting / postponing changes the status: later readers get the fresh one.
const remember = (promise) => promise.then((status) => {
    statusCache = status ? {customer: currentCustomerId(), promise: Promise.resolve(status), at: Date.now()} : null;
    return status;
});

/** Test helper / logout: forget the cached status. */
export function resetTermsStatusCache() {
    statusCache = null;
}

export function acceptTerms(version) {
    return remember(call(() => postCustomerEndpoint("terms/accept", {version}, OPTS)));
}

export function remindTermsLater() {
    return remember(call(() => postCustomerEndpoint("terms/remind-later", {}, OPTS)));
}
